/**
 * Area-overlap detection for the scenario forms.
 *
 * Unusually for `utils/`, this module holds mutable state: two identity
 * caches and one memo slot. That is deliberate and load-bearing (INS-12009) —
 * the callers are TanStack form selectors and validators, which run outside
 * React and so cannot hold a `useMemo`. Please don't "clean up" the
 * singletons; without them this is a 221 ms pairwise scan per keystroke.
 */
import bbox from '@turf/bbox';
import intersect from '@turf/intersect';
import type { Polygon, MultiPolygon } from '@turf/helpers';
import { createGeometryTokenTable } from 'utils/geometry-token-table';
import { createKeyedMemo } from 'utils/keyed-memo';

/**
 * Minimal area item for overlap checking.
 * Generic — not coupled to any specific scenario type.
 */
export interface OverlapCheckItem {
  id: number | string;
  geometryType: string;
  category?: string;
  unitType?: string;
}

type GeometryMap = Record<string | number, GeoJSON.Geometry>;

/** [minX, minY, maxX, maxY] in the geometry's own coordinate space. */
type BoundingBox = [number, number, number, number];

/**
 * The bucket an area competes in for overlap: areas only conflict with areas of
 * the same category, and units only with units of the same unit type. Returns
 * null for areas that take no part in the check at all — slots (point-like), and
 * areas whose grouping value has not been chosen yet.
 *
 * Grouping and cache-key construction both go through this so they cannot drift.
 * @param {OverlapCheckItem} area - The area to classify
 * @return {string | null} The grouping key, or null when the area is excluded
 */
export function overlapGroupKey(area: OverlapCheckItem): string | null {
  if (area.geometryType === 'slot') return null;

  const key =
    area.geometryType === 'area'
      ? `area:${area.category ?? ''}`
      : `unit:${area.unitType ?? ''}`;

  return key === 'area:' || key === 'unit:' ? null : key;
}

// A geometry object's bounding box never changes, and the geometry cache hands
// out stable references, so keying on the object itself makes the box a
// once-per-geometry cost across every call rather than once per pair.
let boundingBoxes = new WeakMap<object, BoundingBox>();

/**
 * Bounding box for a geometry, computed at most once per geometry object.
 * @param {GeoJSON.Geometry} geometry - The geometry to measure
 * @return {BoundingBox | null} The box, or null when it cannot be computed
 */
function boundingBoxFor(geometry: GeoJSON.Geometry): BoundingBox | null {
  const cached = boundingBoxes.get(geometry);
  if (cached !== undefined) return cached;

  try {
    const [minX, minY, maxX, maxY] = bbox(geometry);
    if (
      !Number.isFinite(minX) ||
      !Number.isFinite(minY) ||
      !Number.isFinite(maxX) ||
      !Number.isFinite(maxY)
    ) {
      return null;
    }
    const box: BoundingBox = [minX, minY, maxX, maxY];
    boundingBoxes.set(geometry, box);
    return box;
  } catch {
    // Malformed geometry — fall through to the full intersect, which reports it.
    return null;
  }
}

/**
 * True when two boxes cannot possibly share interior area.
 *
 * The comparison is strict, so boxes that merely touch along an edge are NOT
 * treated as disjoint and still go to `intersect`. That matters: flush
 * neighbouring areas are the common case, and only turf can tell an edge touch
 * (no overlap) from a real overlap.
 * @param {BoundingBox} a - First box
 * @param {BoundingBox} b - Second box
 * @return {boolean} True when the boxes are separated on some axis
 */
function boxesDisjoint(a: BoundingBox, b: BoundingBox): boolean {
  return a[2] < b[0] || b[2] < a[0] || a[3] < b[1] || b[3] < a[1];
}

let computeCount = 0;

/**
 * TEST-ONLY. How many times the uncached pairwise scan has actually run.
 * Used to assert that interactions which cannot change the answer — typing in a
 * field, selecting a different area — recompute zero times.
 * @return {number} The running count
 */
export function __overlapComputeCountForTest(): number {
  return computeCount;
}

/**
 * Checks all pairs of areas within the same grouping key for geometry intersection.
 * For geometryType 'area', the grouping key is `category`.
 * For geometryType 'unit', the grouping key is `unitType`.
 * Slots are excluded from overlap checks (point-like geometries).
 * Returns a Set of overlapping area IDs, or an empty Set if none overlap.
 *
 * Pairs whose bounding boxes are disjoint skip `@turf/intersect` entirely —
 * `intersect` has no cheap rejection of its own and walks both rings regardless.
 * @param {OverlapCheckItem[]} areas - array of area items from form state
 * @param {Record<string | number, GeoJSON.Geometry>} geometries - map of id → geometry
 * @return {Set<number | string>} Set of area IDs that have overlapping geometries
 */
export function getOverlappingAreaIds(
  areas: OverlapCheckItem[],
  geometries: GeometryMap
): Set<number | string> {
  // Deliberate, and shipped: this counter is what `__overlapComputeCountForTest`
  // (declared just above) reads, which is how the memo tests assert zero
  // recomputes without mocking turf. Not stray test state.
  computeCount += 1;

  const overlapping = new Set<number | string>();

  const groups = new Map<string, OverlapCheckItem[]>();
  for (const area of areas) {
    if (!geometries[area.id]) continue;

    const key = overlapGroupKey(area);
    if (key === null) continue;

    const group = groups.get(key) ?? [];
    group.push(area);
    groups.set(key, group);
  }

  for (const group of groups.values()) {
    if (group.length < 2) continue;

    const boxes = group.map((area) => boundingBoxFor(geometries[area.id]));

    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        // Both already flagged — the pair's verdict cannot change the result.
        if (overlapping.has(group[i].id) && overlapping.has(group[j].id)) {
          continue;
        }

        const boxA = boxes[i];
        const boxB = boxes[j];
        if (boxA && boxB && boxesDisjoint(boxA, boxB)) continue;

        try {
          if (
            intersect(
              geometries[group[i].id] as Polygon | MultiPolygon,
              geometries[group[j].id] as Polygon | MultiPolygon
            )
          ) {
            overlapping.add(group[i].id);
            overlapping.add(group[j].id);
          }
        } catch {
          // Invalid geometry — skip pair
        }
      }
    }
  }

  return overlapping;
}

// Identity tokens for geometry objects, so the cache key changes when an area's
// geometry is replaced without having to serialize the coordinates.
//
// Our own table. The DPS map's `map-areas` module holds a second one; the code
// is shared but the instances deliberately are not — see
// `createGeometryTokenTable` for why sharing one would produce stale hits.
const geometryTokens = createGeometryTokenTable();

/**
 * Fingerprints everything `getOverlappingAreaIds` reads: which areas take part,
 * the bucket each competes in, and which geometry each currently points at.
 *
 * Areas the check ignores are left out entirely, so mounting a panel or editing
 * a pace value — which touch neither the id set nor the grouping values — leaves
 * the key untouched.
 * @param {OverlapCheckItem[]} areas - array of area items from form state
 * @param {Record<string | number, GeoJSON.Geometry>} geometries - map of id → geometry
 * @return {string} The cache key
 */
export function overlapCacheKey(
  areas: OverlapCheckItem[],
  geometries: GeometryMap
): string {
  const parts: string[] = [];

  for (const area of areas) {
    const geometry = geometries[area.id];
    if (!geometry) continue;

    const key = overlapGroupKey(area);
    if (key === null) continue;

    // Control characters as separators: they cannot occur in an id, a category
    // or a unit type, so no two different area sets can fingerprint alike.
    parts.push(`${area.id}\u0000${key}\u0000${geometryTokens.token(geometry)}`);
  }

  // Sorted because the dependency is the *set* of (area id, grouping key,
  // geometry) triples, not the array order — the scan returns an
  // order-independent Set. Nothing reorders `areas` today (add appends,
  // remove filters), so this is hardening: it keeps the key matching the
  // stated contract if a reorder or a re-sorted selector ever arrives.
  return parts.sort().join('\u0001');
}

const overlapMemo = createKeyedMemo<Set<number | string>>(
  'getMemoizedOverlappingAreaIds'
);

/**
 * Memoized `getOverlappingAreaIds`. Prefer this at every call site: the scan is
 * O(pairs) with a turf call per candidate pair, while the form store emits on
 * every keystroke and every field mount.
 *
 * Returns the same Set reference on a cache hit, so callers can also rely on it
 * for reference equality — and must not mutate it, hence the readonly type.
 * @param {OverlapCheckItem[]} areas - array of area items from form state
 * @param {Record<string | number, GeoJSON.Geometry>} geometries - map of id → geometry
 * @return {ReadonlySet<number | string>} Set of area IDs that have overlapping geometries
 */
export function getMemoizedOverlappingAreaIds(
  areas: OverlapCheckItem[],
  geometries: GeometryMap
): ReadonlySet<number | string> {
  return overlapMemo.get(overlapCacheKey(areas, geometries), () =>
    getOverlappingAreaIds(areas, geometries)
  );
}

/** TEST-ONLY. Drops the memoized result, the counter and both identity caches. */
export function __resetOverlapMemoForTest(): void {
  overlapMemo.reset();
  computeCount = 0;
  // The identity caches have to go too. A geometry object cached here from a
  // previous test makes a later bbox-call assertion order-dependent, and
  // `retry` is on in vite.config.mts, so it would flip green on the rerun.
  boundingBoxes = new WeakMap<object, BoundingBox>();
  geometryTokens.reset();
}
